import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Link,
  IconButton,
  Collapse,
  Box,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import logo_ada from "../assets/ada-500.png";
import logo_avis from "../assets/avis-500.png";
import logo_europcar from "../assets/europcar-500.png";
import logo_francecars from "../assets/francecars-500.png";
import logo_rentacar from "../assets/rentacar-500.png";

const BestOffersTable = ({ data, expandable = false }) => {
  const [openRows, setOpenRows] = useState({});

  // On trouve l'offre de référence Rentacar
  const rentacarData = data.find((r) => r.name.toLowerCase() === "rentacar");
  let rentacarOffer = null;
  if (rentacarData && rentacarData.success && rentacarData.data?.length > 0) {
    rentacarOffer = rentacarData.data
      .filter((d) => d.tarifTotal !== "ND")
      .sort((a, b) => a.tarifTotal - b.tarifTotal)[0];
    rentacarOffer = { ...rentacarOffer, source: "Rentacar" };
  }

  // On sélectionne les meilleures offres par concurrent (hors Rentacar)
  const bestOffers = data
    .filter(
      (r) =>
        r.success && r.data?.length > 0 && r.name.toLowerCase() !== "rentacar"
    )
    .map((r) => {
      const cheapest = r.data
        .filter((d) => d.tarifTotal !== "ND")
        .sort((a, b) => a.tarifTotal - b.tarifTotal)[0];
      return { ...cheapest, source: r.name, allOffers: r.data };
    });

  // On construit la liste finale (Rentacar en premier)
  const allOffers = rentacarOffer
    ? [{ ...rentacarOffer, allOffers: rentacarData.data }, ...bestOffers]
    : bestOffers;

  // Fonction de calcul de l’écart
  const getEcart = (tarif) => {
    if (!rentacarOffer) return null;
    const ecart =
      ((tarif - rentacarOffer.tarifTotal) / rentacarOffer.tarifTotal) * 100;
    return ecart;
  };

  const toggleRow = (source) => {
    setOpenRows((prev) => ({ ...prev, [source]: !prev[source] }));
  };

  const logoForSource = (source) => {
    switch (source) {
      case "Rentacar":
        return logo_rentacar;
      case "ADA":
        return logo_ada;
      case "Francecars":
        return logo_francecars;
      case "Europcar":
        return logo_europcar;
      case "Avis":
        return logo_avis;
    }

    return logo_rentacar;
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            {expandable && <TableCell />}
            <TableCell>
              <b>Concurrence</b>
            </TableCell>
            <TableCell>
              <b>Name</b>
            </TableCell>
            <TableCell>
              <b>Catégorie</b>
            </TableCell>
            <TableCell align="center">
              <b>Image</b>
            </TableCell>
            <TableCell>
              <b>Tarif total</b>
            </TableCell>
            <TableCell>
              <b>Kilométrage</b>
            </TableCell>
            <TableCell align="center">
              <b>Lien</b>
            </TableCell>
            <TableCell align="center">
              <b>Écart</b>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {allOffers.map((offer, index) => {
            const ecart = getEcart(offer.tarifTotal);
            return (
              <React.Fragment key={index}>
                <TableRow>
                  {expandable && (
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(offer.source)}
                      >
                        {openRows[offer.source] ? (
                          <KeyboardArrowUp />
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton>
                    </TableCell>
                  )}
                  <TableCell>
                    <img
                      src={logoForSource(offer.source)}
                      alt={offer.source}
                      width="100px"
                    />
                  </TableCell>
                  <TableCell>{offer.name}</TableCell>
                  <TableCell>
                    {offer.categorytype
                      ? `${offer.categorytype} - ${offer.categoryLabel}`
                      : offer.categoryLabel || "Non classé"}
                  </TableCell>
                  <TableCell align="center">
                    {offer.image && (
                      <Avatar
                        src={offer.image}
                        variant="rounded"
                        alt={offer.name}
                        sx={{
                          width: 56,
                          height: 56,
                          margin: "auto",
                          "& img": {
                            objectFit: "contain",
                          },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{offer.tarifTotal} €</TableCell>
                  <TableCell>{offer.kilometrage || "ND"}</TableCell>
                  <TableCell align="center">
                    {offer.lienOffre ? (
                      <Link
                        href={offer.lienOffre}
                        target="_blank"
                        rel="noopener"
                      >
                        Voir
                      </Link>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {offer.source === "Rentacar" ? (
                      "—"
                    ) : (
                      <Typography
                        sx={{
                          color:
                            ecart > 0 ? "green" : ecart < 0 ? "red" : "inherit",
                          fontWeight: "bold",
                        }}
                      >
                        {ecart > 0
                          ? `+${ecart.toFixed(0)}%`
                          : ecart
                          ? `${ecart.toFixed(0)}%`
                          : "—"}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>

                {expandable && (
                  <TableRow>
                    <TableCell
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      colSpan={9}
                    >
                      <Collapse
                        in={openRows[offer.source]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box margin={1}>
                          <Typography variant="subtitle2" gutterBottom>
                            Toutes les offres {offer.source} :
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Catégorie</TableCell>
                                <TableCell>Image</TableCell>
                                <TableCell>Tarif total</TableCell>
                                <TableCell>Kilométrage</TableCell>
                                <TableCell>Lien</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {offer.allOffers
                                ?.filter((d) => d.tarifTotal !== "ND")
                                .sort((a, b) => a.tarifTotal - b.tarifTotal)
                                .map((o, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{o.name}</TableCell>
                                    <TableCell>
                                      {o.categorytype
                                        ? `${o.categorytype} - ${o.categoryLabel}`
                                        : o.categoryLabel || "Non classé"}
                                    </TableCell>
                                    <TableCell align="center">
                                      {o.image && (
                                        <Avatar
                                          src={o.image}
                                          variant="rounded"
                                          alt={o.name}
                                          sx={{
                                            width: 56,
                                            height: 56,
                                            "& img": {
                                              objectFit: "contain",
                                            },
                                          }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>{o.tarifTotal} €</TableCell>
                                    <TableCell>
                                      {o.kilometrage || "ND"}
                                    </TableCell>
                                    <TableCell>
                                      {o.lienOffre ? (
                                        <Link
                                          href={o.lienOffre}
                                          target="_blank"
                                          rel="noopener"
                                        >
                                          Voir
                                        </Link>
                                      ) : (
                                        "-"
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BestOffersTable;
